import { cloneElement, useEffect, useRef } from 'react';
import { clsx } from 'clsx';

import type { DragEvent } from 'react';

import styles from 'components/Sortable/Sortable.module.css';
import { useSortable } from 'components/Sortable/SortableProvider';

export type OnSort = (indices: number[]) => void;

interface SortableContainerProps {
  scrollable?: boolean;
  hideNumbers?: boolean;
  onSort?: OnSort;
}

export function SortableContainer({ scrollable, hideNumbers, onSort }: SortableContainerProps) {
  const { clone, draggingTarget, drop, sortableItems, setDraggingTarget, reset, indices } = useSortable();
  const containerRef = useRef<HTMLUListElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const indicesRef = useRef<number[]>(indices);

  function getTargetIndex(event: DragEvent<HTMLLIElement>) {
    const sortableContainer = containerRef.current;

    if (!sortableContainer?.hasChildNodes()) return;

    const listItemNode = event.currentTarget;
    const childNodes = Array.from(sortableContainer.childNodes);
    const targetIndex = childNodes.findIndex((node) => node.isEqualNode(listItemNode));

    return targetIndex;
  }

  function handleDrop(event: DragEvent<HTMLLIElement>) {
    const targetIndex = getTargetIndex(event);

    if (targetIndex === undefined) return;

    if (clone && wrapperRef.current) wrapperRef.current.removeChild(clone);

    drop(targetIndex);
  }

  function handleDragOver(event: DragEvent<HTMLLIElement>) {
    // Prevent default to allow drop
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (clone) {
      const { clientX, clientY } = event;
      const { x, y, width, height } = event.currentTarget.getBoundingClientRect();
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const grabPosition = height / 2 - (clientY - y);

      // lock drag image movement to x-axis and mouse pointer
      clone.setAttribute(
        'style',
        `
        left: ${clientX - (clientX - x)}px;
        top: ${y - grabPosition}px;
        height: ${height}px;
        width: ${width}px;
        visibility: visible;
      `
      );
    }

    const targetIndex = getTargetIndex(event);

    if (targetIndex === undefined || targetIndex === draggingTarget) return;

    setDraggingTarget(targetIndex);
  }

  function handleContainerDragLeave(event: DragEvent<HTMLUListElement>) {
    const { relatedTarget } = event;

    const within = relatedTarget !== null && containerRef.current?.contains(relatedTarget as Node);

    if (!within) {
      reset();

      if (clone) {
        clone.setAttribute('style', 'visibility: hidden;');
      }
    }
  }

  useEffect(() => {
    if (!clone || !wrapperRef.current) return;

    wrapperRef.current.appendChild(clone);

    return function () {
      clone.remove();
    };
  }, [clone]);

  useEffect(() => {
    if (!onSort) return;

    if (indices.valueOf() !== indicesRef.current.valueOf()) {
      onSort(indices);

      indicesRef.current = indices;
    }
  }, [indices, indicesRef, onSort]);

  return (
    <div
      className={clsx(styles['sortable__wrapper'], { [styles['sortable__wrapper--scrollable']]: scrollable })}
      ref={wrapperRef}
    >
      <ul
        className={clsx(styles['sortable__container'], {
          [styles['sortable__container--drag-over']]: clone !== undefined,
        })}
        onDragLeave={handleContainerDragLeave}
        ref={containerRef}
        role="presentation"
      >
        {sortableItems.map((child, index) => (
          <li
            className={clsx(styles['sortable__drop-target'], {
              [styles['sortable__drop-target--no-number']]: hideNumbers,
            })}
            draggable={false}
            key={index}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="presentation"
          >
            {cloneElement(child, { index })}
          </li>
        ))}
      </ul>
    </div>
  );
}
